import { useEffect, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus, Coins, LineChart, Crown } from "lucide-react";

export default function StockMarket() {
  const [mkt, setMkt] = useState(null);
  const [lb, setLb] = useState(null);
  const [size, setSize] = useState(1);
  const [busy, setBusy] = useState(null);

  const load = () => {
    fz.stock.market().then(({ data }) => setMkt(data)).catch(() => {});
    fz.stock.leaderboard().then(({ data }) => setLb(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!mkt) return <div className="titans-card p-6 text-titans-silver text-sm">Opening the market…</div>;

  const pnl = mkt.portfolio_value - mkt.start_cash;

  const trade = async (id, action) => {
    if (busy) return;
    setBusy(id + action);
    try {
      const { data } = await fz.stock.trade(id, action, size);
      if (data.award) toastAward(data.award);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Trade failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="fz-stock">
      <div className="titans-card p-5 md:p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <LineChart size={16} className="text-titans-blue" />
            <span className="font-display text-xl uppercase">Camp Stock Market</span>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="text-right">
              <div className="font-display text-2xl leading-none tabular-nums flex items-center gap-1.5 justify-end">
                <Coins size={16} className="text-amber-500" />
                <span data-testid="fz-stock-cash">{mkt.cash.toLocaleString()}</span>
              </div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-0.5">Cash</div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl leading-none tabular-nums" data-testid="fz-stock-value">{mkt.portfolio_value.toLocaleString()}</div>
              <div className={`font-cond uppercase tracking-wider text-[10px] mt-0.5 ${pnl > 0 ? "text-emerald-600" : pnl < 0 ? "text-titans-red" : "text-titans-silver"}`}>
                Portfolio {pnl > 0 ? `+${pnl}` : pnl < 0 ? pnl : "even"}
              </div>
            </div>
            <div className="flex gap-1">
              {[1, 5, 10].map((n) => (
                <button
                  key={n}
                  onClick={() => setSize(n)}
                  data-testid={`fz-stock-size-${n}`}
                  className={`px-3 py-1.5 font-cond uppercase tracking-wider text-xs border transition-colors ${
                    size === n ? "bg-titans-navy border-titans-navy text-white" : "border-[#E2E8F0] text-titans-silver hover:border-titans-blue"
                  }`}
                >
                  ×{n}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="text-titans-silver text-xs font-body mt-2">
          Every fan starts with {mkt.start_cash.toLocaleString()} coins. Prices move with fan demand — buy players you believe in before the hype hits.
        </p>

        <div className="mt-4 space-y-1.5">
          {mkt.stocks.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-lg border border-[#E2E8F0] px-3 py-2.5" data-testid={`fz-stock-row-${s.id}`}>
              <span className="font-display text-xs w-10 shrink-0 text-center rounded px-1 py-0.5 bg-[#F1F5F9] text-titans-muted">{s.pos}</span>
              <span className="font-cond uppercase tracking-wide text-sm flex-1 truncate">{s.name}</span>
              {s.held > 0 && (
                <span className="font-cond uppercase tracking-wider text-[9px] bg-titans-blue/10 text-titans-blue px-2 py-0.5 rounded-full shrink-0" data-testid={`fz-stock-held-${s.id}`}>
                  {s.held} held
                </span>
              )}
              <span className={`inline-flex items-center gap-0.5 font-cond text-[11px] w-14 justify-end shrink-0 ${s.change > 0 ? "text-emerald-600" : s.change < 0 ? "text-titans-red" : "text-titans-silver"}`}>
                {s.change > 0 ? <TrendingUp size={11} /> : s.change < 0 ? <TrendingDown size={11} /> : <Minus size={11} />}
                {s.change > 0 ? `+${s.change}` : s.change || "0"}
              </span>
              <span className="font-display text-xl text-titans-blue tabular-nums w-14 text-right shrink-0" data-testid={`fz-stock-price-${s.id}`}>{s.price}</span>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => trade(s.id, "buy")}
                  disabled={!!busy || mkt.cash < s.price * size}
                  data-testid={`fz-stock-buy-${s.id}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 font-cond uppercase tracking-wider text-[10px] rounded-md disabled:opacity-35"
                >
                  Buy
                </button>
                <button
                  onClick={() => trade(s.id, "sell")}
                  disabled={!!busy || s.held < size}
                  data-testid={`fz-stock-sell-${s.id}`}
                  className="bg-titans-red hover:bg-titans-red-hover text-white px-3 py-1.5 font-cond uppercase tracking-wider text-[10px] rounded-md disabled:opacity-35"
                >
                  Sell
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {lb && lb.entries.length > 0 && (
        <div className="titans-card p-5 md:p-6" data-testid="fz-stock-leaderboard">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={15} className="text-titans-blue" />
            <span className="font-display text-xl uppercase">Market Movers</span>
          </div>
          <div className="space-y-1">
            {lb.entries.slice(0, 5).map((e, i) => (
              <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                <span className={`font-display text-base w-6 text-center ${i === 0 ? "text-titans-red" : "text-titans-silver"}`}>{i + 1}</span>
                <span className="font-cond uppercase tracking-wide text-sm flex-1 truncate">{e.name}</span>
                <span className="font-display text-lg text-titans-blue tabular-nums">{e.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
